import { http } from '@/shared/api/http';
import type { DeepRequired, Schema, WithRequired } from '@/shared/api';
import type {
  ActiveTimerDto,
  CreateTaskData,
  Paginated,
  Task,
  TaskDetails,
  TaskStatus,
  TaskType,
} from '@/shared/types/task';
import { Project } from '../../../hooks/groups/useProjectsWithTasks';
import {
  getNumberValue,
  getStringValue,
  normalizePriority,
} from '../../../utils/dataNormalizers';

// Обновляем тип параметров с новыми фильтрами
export type FetchTasksParams = {
  page?: number;
  perPage?: number;
  search?: string;
  status?: string;
  responsibleId?: number;
  createdBy?: number;
  period?: 'day' | 'week' | 'month';
  sort?: string;
  onlyMyTasks?: boolean;
  onlyAuditor?: boolean;
  onlyAccomplice?: boolean;
  onlyCreator?: boolean;
  currentUserId?: number;
  groupId?: number;
  groupIds?: number[];
  assigneeIds?: number[];
  /** Мультивыбор отделов (BE-3.2): исполнители из любого из отделов (OR). */
  departmentIds?: number[];

  dateFrom?: string; // Новый фильтр DATE_FROM
  dateTo?: string; // Новый фильтр DATE_TO
  includeOverdue?: boolean;

  control?: '0' | '1';

  /** Список (не канбан/календарь): использовать серверную пагинацию/фильтрацию
   *  /tasks/list вместо клиентской. Откат на клиент — см. canServerPaginate. */
  serverPaginated?: boolean;
};

export type ChecklistItemFileUpload = {
  content: string;
  name: string;
  targetField?: string;
};

export type ChecklistItemFilePayload = {
  /** Один файл (старый вариант, бэком поддерживается). */
  file?: ChecklistItemFileUpload;
  /** Несколько файлов за один запрос (POST .../files принимает массив files). */
  files?: ChecklistItemFileUpload[];
  fileId?: number;
};

export type ChecklistItemFile = {
  downloadUrl: string;
  id: number;
  isImage?: boolean;
  mimeType: string;
  name: string;
  size: number;
  source?: string;
  url?: string;
};

/** New base64 file to attach directly when creating/updating an item. */
export type ChecklistItemInlineFile = {
  name: string;
  content: string;
};

export type ChecklistItemCreatePayload = {
  isComplete: boolean;
  members: number[];
  parentId: number;
  title: string;
  /** Ids of already-uploaded files to attach. */
  attachments?: number[];
  /** Files to upload and attach in the same request (base64). */
  files?: ChecklistItemInlineFile[];
};

export type ChecklistItemUpdatePayload = {
  isComplete: boolean;
  members: number[];
  parentId: number;
  title: string;
  attachments?: number[];
  files?: ChecklistItemInlineFile[];
};

export type ChecklistCreatePayload = {
  title: string;
};

const getTitleValue = (title: ANY): string => {
  const value = getStringValue(title);
  return value || 'Без названия';
};

export type FileItem = WithRequired<
  Schema<'tasks_module_internal_models.FileDTO'>,
  'id' | 'name' | 'size' | 'mimeType' | 'downloadUrl'
>;

/**
 * Элемент /tasks/list. База — models.TaskShort из спеки; дополнительные поля
 * маппер разбирает потому, что тот же mapBackendTaskToTask вызывается и на
 * ответах карточки/доски. В TaskShort их нет — если бэк перестанет их слать
 * в списке, поведение не изменится (все обращения через ??).
 */
type BackendTask = Omit<
  WithRequired<
    Schema<'tasks_module_internal_models.TaskShort'>,
    'ID' | 'Status'
  >,
  'project'
> & {
  /** Своя форма проекта: доменный Project, а не ProjectShort из спеки. */
  project: Project;
  Description?: string;
  DescriptionInBbcode?: 'Y' | 'N';
  ParentID?: number;
  ChangedDate?: string;
  StatusChangedDate?: string;
  ActivityDate?: string;
  StageID?: number;
  UfCrmTask?: string[];
  TagsCSV?: string;
  Replicate: 'Y' | 'N';
  TaskControl: 'Y' | 'N';
  activeTimers?: ActiveTimerDto[];
  files?: FileItem[];
  requiresApproval?: boolean | 'Y' | 'N';
};

type BackendListResponse = { result?: BackendTask[] };

// /tasks/list с серверной пагинацией отдаёт total/hasMore
// (internal_handler_http.TaskListResponse).
type BackendListResponsePaged = Omit<
  Schema<'internal_handler_http.TaskListResponse'>,
  'result'
> & { result?: BackendTask[] };

/** Маппинг «сырой» задачи бэка в доменную Task (общий для клиентского и
 *  серверного путей fetchTasks). */
function mapBackendTaskToTask(t: BackendTask, currentUserId?: number): Task {
  const baseTask: Omit<Task, 'type'> = {
    id: String(t.ID),
    title: getTitleValue(t.Title),
    status: mapStatus(t.Status),
    priority: normalizePriority(t.Priority ?? 0),
    assigneeId: getNumberValue(t.ResponsibleID),
    assigneeName: null,
    createdBy: getNumberValue(t.CreatedBy),
    dateStart: t.DateStart ?? null,
    startDatePlan: t.StartDatePlan ?? null,
    endDatePlan: t.EndDatePlan ?? null,
    dueDate: t.Deadline ?? null,
    updatedAt:
      t.ChangedDate ??
      t.ActivityDate ??
      t.CreatedDate ??
      new Date().toISOString(),
    createdAt: t.CreatedDate || null,
    backendStatus: t.Status,

    hasResult: (t as ANY).hasResult,
    requiresApproval: (t as ANY).requiresApproval,

    files:
      t.files?.map((f: ANY) => ({
        id: f.id,
        name: f.name,
        size: f.size,
        mimeType: f.mimeType,
        downloadUrl: f.downloadUrl,
        url: f.url,
      })) ?? [],
    fileStats: {
      images: t.fileStats?.images ?? 0,
      videos: t.fileStats?.videos ?? 0,
      documents: t.fileStats?.documents ?? 0,
      others: t.fileStats?.others ?? 0,
    },
    description: getStringValue(t.Description),
    groupId: getNumberValue(t.GroupID)!,
    accomplices: t.accomplices ?? null,
    auditors: t.auditors ?? null,
    parentId: getNumberValue(t.ParentID),
    timeEstimate: t.TimeEstimate ?? null,
    durationFact: getNumberValue(t.DurationFact),
    progressPercent:
      typeof t.progressPercent === 'number' ? t.progressPercent : null,
    costAmount: typeof t.costAmount === 'number' ? t.costAmount : null,
    costCurrency: t.costCurrency ?? null,
    activeTimers: Array.isArray(t.activeTimers) ? t.activeTimers : [],
    UfCrmTask: getStringValue(t.UfCrmTask),
    tagsCSV: Array.isArray((t as ANY).tags)
      ? (t as ANY).tags.join(',')
      : (t.TagsCSV ?? ''),
    project: t.project,

    checklist: [],
    elapsed: [],
    comments: [],
    replicate: t.Replicate,
    controlling: t.TaskControl,
    isMeeting: !!(t as ANY).isMeeting,
  };

  return {
    ...baseTask,
    type: determineTaskType(baseTask, currentUserId),
  };
}

// Поля сортировки колонок списка → enum сортировки /tasks/list
// (created|deadline|title|status|priority). Поля вне enum (имя исполнителя)
// сервер не сортирует — сорт по ним просто не применяется (колонка не кликабельна).
const SERVER_SORT_FIELD: Record<string, string> = {
  title: 'title',
  status: 'status',
  priority: 'priority',
  dueDate: 'deadline',
  deadline: 'deadline',
  updatedAt: 'created',
  createdAt: 'created',
  created: 'created',
};

/** Разбор `filters.sort` («-updatedAt») в серверные {sort, order}. null — поле
 *  не поддерживается сервером (нужен клиентский путь). */
function toServerSort(
  sort?: string
): { sort: string; order: 'asc' | 'desc' } | null {
  if (!sort) {
    return null;
  }
  const desc = sort.startsWith('-');
  const field = desc ? sort.slice(1) : sort;
  const mapped = SERVER_SORT_FIELD[field];
  return mapped ? { sort: mapped, order: desc ? 'desc' : 'asc' } : null;
}

/** Единый серверный набор фильтров для /tasks/list (форматы по swagger:
 *  RESPONSIBLE_IDS/GROUP_IDS — csv). Общий для серверной пагинации и фолбэка,
 *  чтобы фильтровал БЭК, а не память. Без sort/page/limit — добавляет вызывающий. */
function buildListFilterQuery(params: FetchTasksParams): ANY {
  const deadlineFrom =
    params.includeOverdue === true ? undefined : params.dateFrom || undefined;

  const q: ANY = {
    search: params.search || undefined,
    PERIOD: params.period || undefined,
    DEADLINE_FROM: deadlineFrom,
    DEADLINE_TO: params.dateTo || undefined,
    CREATED_BY: params.createdBy || undefined,
  };

  // Статус: «на контроле» → STATUS=4; «-5» → исключить завершённые; иначе
  // конкретный STATUS (params.status приходит числом-кодом бэка).
  if (params.control === '1') {
    q.STATUS = 4;
  } else if (params.status === '-5') {
    q.exclude_completed = true;
  } else if (params.status && params.status !== ' ') {
    q.STATUS = Number(params.status);
  }

  // Исполнители: мультивыбор → RESPONSIBLE_IDS (csv); явный один →
  // RESPONSIBLE_ID; иначе «Мои» → текущий юзер.
  if (params.assigneeIds && params.assigneeIds.length > 0) {
    q.RESPONSIBLE_IDS = params.assigneeIds.join(',');
  } else if (params.responsibleId) {
    q.RESPONSIBLE_ID = params.responsibleId;
  } else if (params.onlyMyTasks && params.currentUserId) {
    q.RESPONSIBLE_ID = params.currentUserId;
  }

  // Роли текущего пользователя.
  if (params.onlyCreator) {
    q.only_creator = true;
  }
  if (params.onlyAuditor) {
    q.only_auditor = true;
  }
  if (params.onlyAccomplice) {
    q.only_accomplice = true;
  }

  // Группы: множественный выбор → GROUP_IDS (csv); одиночная → GROUP_ID.
  if (params.groupIds && params.groupIds.length > 1) {
    q.GROUP_IDS = params.groupIds.join(',');
  } else {
    const singleGroup = params.groupIds?.[0] ?? params.groupId;
    if (singleGroup) {
      q.GROUP_ID = singleGroup;
    }
  }

  // Отделы (BE-3.2): CSV, OR по исполнителям.
  if (params.departmentIds && params.departmentIds.length > 0) {
    q.departmentIds = params.departmentIds.join(',');
  }

  Object.keys(q).forEach((key) => {
    if (q[key] === undefined) {
      delete q[key];
    }
  });
  return q;
}

/** Серверная пагинация/фильтрация через /tasks/list (page/limit/total).
 *  Никакой клиентской фильтрации/сортировки/среза — всё делает бэк. */
async function fetchTasksServerSide(
  params: FetchTasksParams,
  page: number,
  perPage: number
): Promise<Paginated<Task>> {
  const q = buildListFilterQuery(params);
  q.page = page;
  q.limit = perPage;

  // Сортировка (updatedAt→created, dueDate→deadline и т.п.).
  const srv = toServerSort(params.sort);
  if (srv) {
    q.sort = srv.sort;
    q.order = srv.order;
  }

  const { data } = await http.get<BackendListResponsePaged>('/tasks/list', {
    params: q,
  });

  const items = (data?.result ?? []).map<Task>((t) =>
    mapBackendTaskToTask(t, params.currentUserId)
  );
  const total = data?.total ?? items.length;

  return { items, page, perPage, total };
}

// Функция для определения типа задачи
function determineTaskType(
  task: Omit<Task, 'type'>,
  currentUserId?: number
): TaskType {
  const now = new Date();

  // 1. Срочные - Только мои задачи + существующий фильтр
  if (currentUserId && task.assigneeId === currentUserId) {
    if (task.dueDate) {
      const dueDate = new Date(task.dueDate);

      // Проверяем, что дедлайн сегодня или просрочен (учитывая время)
      const isOverdue = dueDate < now;
      const isToday = dueDate.toDateString() === now.toDateString();

      if (isToday || isOverdue) {
        // Для просроченных задач сразу возвращаем "urgent"
        if (isOverdue) {
          return 'urgent';
        }

        // Для задач на сегодня проверяем процент времени для выполнения
        if (isToday && task.timeEstimate && task.createdAt) {
          const createdAt = new Date(task.createdAt);
          const timePassed = now.getTime() - createdAt.getTime();
          const timeEstimateMs = task.timeEstimate * 1000;
          const timeRemaining = timeEstimateMs - timePassed;

          // Если осталось меньше 20% времени
          if (timeRemaining > 0 && timeRemaining < 0.2 * timeEstimateMs) {
            return 'urgent';
          }
        }
      }
    }
  }

  // 2. Важные - Только мои задачи + существующий фильтр
  if (currentUserId && task.assigneeId === currentUserId) {
    if (task.dueDate) {
      const dueDate = new Date(task.dueDate);

      // Важные - задачи на сегодня (только по дате, без учета времени)
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const dueDateOnly = new Date(dueDate);
      dueDateOnly.setHours(0, 0, 0, 0);

      if (dueDateOnly.getTime() === today.getTime()) {
        return 'important';
      }
    }
  }

  // 3. Регулярные - Только мои задачи + существующий фильтр
  if (currentUserId && task.assigneeId === currentUserId) {
    if (task.replicate === 'Y') {
      return 'regular';
    }
  }

  // 4. Помогаю - Только где соисполнитель
  if (
    currentUserId &&
    task.accomplices &&
    task.accomplices.includes(currentUserId)
  ) {
    return 'helping';
  }

  // 5. Контролирую - backendStatus === 4 ИЛИ наблюдатель, постановщик или соисполнитель
  if (currentUserId) {
    const isAuditor = task.auditors && task.auditors.includes(currentUserId);
    const isCreator = task.createdBy === currentUserId;
    const isAccomplice =
      task.accomplices && task.accomplices.includes(currentUserId);

    if (task.backendStatus === 4 && (isAuditor || isCreator || isAccomplice)) {
      return 'controlling';
    }
  }

  // 6. На потом - Только свои отложенные, без крайнего срока
  if (currentUserId && !task.dueDate && currentUserId === task.assigneeId) {
    return 'later';
  }

  // 7. Обычные задачи
  return 'normal';
}

// Обновим функцию fetchTasks
export async function fetchTasks(params: FetchTasksParams) {
  const page = params.page ?? 1;
  const perPage = params.perPage ?? 10;

  // Список (serverPaginated) — ВСЕГДА серверная пагинация/фильтрация. Сорт по
  // полю без серверного enum (имя исполнителя) просто не применяется
  // (toServerSort → null, бэк отдаёт в своём порядке), но данные полные и
  // отфильтрованы. Раньше такой сорт уводил на клиентский путь ниже — тот грузил
  // дефолтные 50 задач и резал фильтры/поиск/пагинацию в памяти (терял задачи).
  if (params.serverPaginated) {
    return fetchTasksServerSide(params, page, perPage);
  }

  // Прочие экраны (мобилка/встречи/поиск родителя/страницы проектов): те же
  // серверные фильтры, что и у списка (csv) — теперь фильтрует БЭК, а не память.
  // Раньше грузились дефолтные 50 задач БЕЗ фильтров и резались клиентом (при
  // >50 терялись задачи, поиск сужался до заголовка). limit — максимум бэка (500).
  // Сортировка по полю без серверного enum и пагинация — на клиенте по полному
  // отфильтрованному набору.
  const q = buildListFilterQuery(params);
  q.limit = 500;

  const { data } = await http.get<BackendListResponse>('/tasks/list', {
    params: q,
  });

  let items = (data?.result ?? []).map<Task>((t) =>
    mapBackendTaskToTask(t, params.currentUserId)
  );

  if (params.sort) {
    const desc = params.sort.startsWith('-');
    const field = desc ? params.sort.slice(1) : params.sort;
    items = items.slice().sort((a: ANY, b: ANY) => {
      const av = a[field],
        bv = b[field];
      if (av == null && bv == null) {
        return 0;
      }
      if (av == null) {
        return 1;
      }
      if (bv == null) {
        return -1;
      }
      if (av < bv) {
        return desc ? 1 : -1;
      }
      if (av > bv) {
        return desc ? -1 : 1;
      }
      return 0;
    });
  }

  const total = items.length;
  const start = (page - 1) * perPage;
  const paged = items.slice(start, start + perPage);

  return { items: paged, page, perPage, total };
}

function pickDate(v: ANY): string | null {
  if (!v) {
    return null;
  }
  if (typeof v === 'string') {
    return v;
  }
  if (
    typeof v === 'object' &&
    typeof v.Time === 'string' &&
    (v.Valid === undefined || v.Valid === true)
  ) {
    return v.Time;
  }
  return null;
}

export function mapStatus(code: number): TaskStatus {
  switch (code) {
    case 2:
      return 'in_progress';
    case 3:
      return 'open';
    case 4:
      return 'waiting_review';
    case 5:
      return 'done';
    case 6:
      return 'blocked';
    default:
      return 'open'; // по умолчанию
  }
}

/* ───────────────── Канбан-доска (/tasks/board) ───────────────── */

export type BoardColumnKey =
  | 'on_control'
  | 'overdue'
  | 'today'
  | 'tomorrow'
  | 'this_week'
  | 'later'
  | 'no_deadline';

/** Сырой элемент колонки доски. */
type TaskShortRaw = WithRequired<
  Schema<'tasks_module_internal_models.TaskShort'>,
  'ID' | 'Status'
>;

export type BoardFilterParams = {
  search?: string;
  responsibleId?: number;
  responsibleIds?: number[];
  createdBy?: number;
  onlyCreator?: boolean;
  onlyAuditor?: boolean;
  onlyAccomplice?: boolean;
  groupId?: number;
  groupIds?: number[];
  status?: number;
  excludeCompleted?: boolean;
  projectEntityId?: number;
  projectEntityTypeId?: number;
  limit?: number;
  page?: number;
};

export type BoardTaskColumn = {
  key: BoardColumnKey;
  total: number;
  hasMore: boolean;
  items: Task[];
};

type BoardColumnRaw = {
  key: BoardColumnKey;
  total: number;
  hasMore: boolean;
  items: TaskShortRaw[];
};

function boardQueryParams(p: BoardFilterParams) {
  const q: ANY = {
    SEARCH: p.search || undefined,
    RESPONSIBLE_ID: p.responsibleId || undefined,
    // Бэк ждёт множественные фильтры через запятую (swagger: collectionFormat
    // csv). Массив axios сериализует иначе — фильтр молча игнорировался.
    RESPONSIBLE_IDS: p.responsibleIds?.length
      ? p.responsibleIds.join(',')
      : undefined,
    CREATED_BY: p.createdBy || undefined,
    only_creator: p.onlyCreator || undefined,
    only_auditor: p.onlyAuditor || undefined,
    only_accomplice: p.onlyAccomplice || undefined,
    GROUP_ID: p.groupId || undefined,
    GROUP_IDS: p.groupIds?.length ? p.groupIds.join(',') : undefined,
    STATUS: p.status ?? undefined,
    exclude_completed: p.excludeCompleted || undefined,
    PROJECT_ENTITY_ID: p.projectEntityId || undefined,
    PROJECT_ENTITY_TYPE_ID: p.projectEntityTypeId || undefined,
    limit: p.limit || undefined,
    page: p.page || undefined,
  };
  Object.keys(q).forEach((k) => q[k] === undefined && delete q[k]);
  return q;
}

/** TaskShort → Task для карточки канбана. project нормализуем под форму,
 *  которую ждёт карточка (project.Title.String и company.Title). */
export function mapTaskShort(t: TaskShortRaw, currentUserId?: number): Task {
  const base: Omit<Task, 'type'> = {
    id: String(t.ID),
    title: getTitleValue(t.Title) || 'Без названия',
    status: mapStatus(t.Status),
    backendStatus: t.Status,
    priority: normalizePriority(t.Priority ?? 0),
    assigneeId: getNumberValue(t.ResponsibleID),
    assigneeName: null,
    createdBy: getNumberValue(t.CreatedBy),
    dateStart: t.DateStart ?? null,
    startDatePlan: t.StartDatePlan ?? null,
    endDatePlan: t.EndDatePlan ?? null,
    dueDate: t.Deadline ?? null,
    updatedAt: t.CreatedDate ?? new Date().toISOString(),
    createdAt: t.CreatedDate ?? null,
    description: null,
    groupId: getNumberValue(t.GroupID) ?? 0,
    accomplices: Array.isArray(t.accomplices) ? t.accomplices : null,
    auditors: Array.isArray(t.auditors) ? t.auditors : null,
    parentId: null,
    timeEstimate: typeof t.TimeEstimate === 'number' ? t.TimeEstimate : null,
    durationFact: typeof t.DurationFact === 'number' ? t.DurationFact : null,
    progressPercent:
      typeof t.progressPercent === 'number' ? t.progressPercent : null,
    costAmount: typeof t.costAmount === 'number' ? t.costAmount : null,
    costCurrency: t.costCurrency ?? null,
    imagePreviews: Array.isArray(t.imagePreviews) ? t.imagePreviews : [],
    activeTimers: [],
    UfCrmTask: '',
    tagsCSV: Array.isArray(t.tags) ? t.tags.join(',') : '',
    project: { Title: { String: getStringValue(t.project?.Title) } } as ANY,
    company: t.project?.companyTitle
      ? ({ Title: getStringValue(t.project.companyTitle) } as ANY)
      : undefined,
    checklist: [],
    elapsed: [],
    comments: [],
    replicate: 'N',
    controlling: 'N',
    isMeeting: !!t.isMeeting,
    fileStats: {
      images: t.fileStats?.images ?? 0,
      videos: t.fileStats?.videos ?? 0,
      documents: t.fileStats?.documents ?? 0,
      others: t.fileStats?.others ?? 0,
    },
    files: [],
    hasResult: t.hasResult,
    awaitingApproval: t.awaitingApproval,
  };

  return { ...base, type: determineTaskType(base, currentUserId) };
}

/** GET /tasks/board — все 7 колонок сразу. */
export async function fetchTaskBoard(
  params: BoardFilterParams,
  currentUserId?: number
): Promise<BoardTaskColumn[]> {
  const { data } = await http.get<{ columns: BoardColumnRaw[] }>(
    '/tasks/board',
    { params: boardQueryParams(params) }
  );
  return (data?.columns ?? []).map((c) => ({
    key: c.key,
    total: c.total ?? 0,
    hasMore: !!c.hasMore,
    items: (c.items ?? []).map((t) => mapTaskShort(t, currentUserId)),
  }));
}

/** GET /tasks/board/{column} — дозагрузка одной колонки. */
export async function fetchTaskBoardColumn(
  column: BoardColumnKey,
  params: BoardFilterParams,
  currentUserId?: number
): Promise<{ total: number; hasMore: boolean; items: Task[] }> {
  const { data } = await http.get<Omit<BoardColumnRaw, 'key'>>(
    `/tasks/board/${column}`,
    { params: boardQueryParams(params) }
  );
  return {
    total: data?.total ?? 0,
    hasMore: !!data?.hasMore,
    items: (data?.items ?? []).map((t) => mapTaskShort(t, currentUserId)),
  };
}

/* ───────────────── Массовый ресчедулинг (/tasks/bulk-reschedule) ───────── */

/** BE-3.5: одна задача — свои значения (в отличие от bulk-action). Передаём
 *  только меняющиеся поля. Каскад по зависимостям фронт считает сам. */
export interface RescheduleChange {
  taskId: number;
  startDatePlan?: string;
  endDatePlan?: string;
  deadline?: string;
  responsibleId?: number;
}

export async function bulkReschedule(changes: RescheduleChange[]): Promise<{
  succeededIds: number[];
  failed: { taskId: number; error: string }[];
}> {
  const { data } = await http.post('/tasks/bulk-reschedule', { changes });
  return {
    succeededIds: data?.succeededIds ?? [],
    failed: data?.failed ?? [],
  };
}

/* ───────────────── Календарь (/tasks/calendar) ───────────────── */

/** Сырой элемент облегчённого ответа /tasks/calendar — ровно те поля, что
 *  реально рендерит календарь: раскладка по Deadline (+ TimeEstimate) + карточка
 *  в ячейке + тултип по наведению. Тяжёлого обогащения
 *  (files/fileStats/project/activeTimers/description) здесь нет — см. заказ бэку. */
// Облегчённый ответ /tasks/calendar: deps — ID задач-предшественников
// (blockedBy) для связей Ганта.
type TaskCalendarRaw = WithRequired<
  Schema<'tasks_module_internal_models.TaskCalendarShort'>,
  'ID' | 'Status'
>;

/** Параметры /tasks/calendar (те же фильтры, что у серверного /tasks/list, но
 *  без page/limit/sort — эндпоинт ограничен диапазоном дедлайнов и не пагинирует). */
function calendarQueryParams(params: FetchTasksParams) {
  const q: ANY = {
    SEARCH: params.search || undefined,
    DEADLINE_FROM: params.dateFrom || undefined,
    DEADLINE_TO: params.dateTo || undefined,
  };

  // Статус — та же логика, что в серверном /tasks/list.
  if (params.control === '1') {
    q.STATUS = 4;
  } else if (params.status === '-5') {
    q.exclude_completed = true;
  } else if (params.status && params.status !== ' ') {
    q.STATUS = Number(params.status);
  }

  // Исполнители: явный выбор → RESPONSIBLE_IDS; иначе «Мои» → текущий юзер.
  if (params.assigneeIds && params.assigneeIds.length > 0) {
    q.RESPONSIBLE_IDS = params.assigneeIds.join(',');
  } else if (params.onlyMyTasks && params.currentUserId) {
    q.RESPONSIBLE_ID = params.currentUserId;
  }

  if (params.onlyCreator) {
    q.only_creator = true;
  }
  if (params.onlyAuditor) {
    q.only_auditor = true;
  }
  if (params.onlyAccomplice) {
    q.only_accomplice = true;
  }

  // Группы: множественный выбор → GROUP_IDS; одиночная → GROUP_ID.
  if (params.groupIds && params.groupIds.length > 1) {
    q.GROUP_IDS = params.groupIds.join(',');
  } else {
    const singleGroup = params.groupIds?.[0] ?? params.groupId;
    if (singleGroup) {
      q.GROUP_ID = singleGroup;
    }
  }

  // Отделы (BE-3.2): CSV, OR по исполнителям.
  if (params.departmentIds && params.departmentIds.length > 0) {
    q.departmentIds = params.departmentIds.join(',');
  }

  Object.keys(q).forEach((key) => {
    if (q[key] === undefined) {
      delete q[key];
    }
  });
  return q;
}

/** TaskCalendarRaw → Task для карточек/тултипа календаря. Имена и аватары
 *  исполнителя/постановщика тултип резолвит сам по id (useUsers.useById), поэтому
 *  в ответе только id-шники. Отсутствующие в облегчённом ответе поля заполняем
 *  нейтральными значениями. */
function mapCalendarTask(t: TaskCalendarRaw, currentUserId?: number): Task {
  const base: Omit<Task, 'type'> = {
    id: String(t.ID),
    title: getTitleValue(t.Title),
    status: mapStatus(t.Status),
    backendStatus: t.Status,
    priority: normalizePriority(t.Priority ?? 0),
    assigneeId: getNumberValue(t.ResponsibleID),
    assigneeName: null,
    createdBy: getNumberValue(t.CreatedBy),
    dateStart: t.DateStart ?? null,
    startDatePlan: t.StartDatePlan ?? null,
    endDatePlan: t.EndDatePlan ?? null,
    dueDate: t.Deadline ?? null,
    updatedAt: t.CreatedDate ?? new Date().toISOString(),
    createdAt: t.CreatedDate ?? null,
    description: null,
    groupId: getNumberValue(t.GroupID) ?? 0,
    accomplices: Array.isArray(t.accomplices) ? t.accomplices : null,
    auditors: Array.isArray(t.auditors) ? t.auditors : null,
    parentId: null,
    timeEstimate: typeof t.TimeEstimate === 'number' ? t.TimeEstimate : null,
    durationFact: typeof t.durationFact === 'number' ? t.durationFact : null,
    deps: Array.isArray(t.deps) ? t.deps : null,
    riskState: t.riskState ?? null,
    progressPercent: null,
    costAmount: null,
    costCurrency: null,
    activeTimers: [],
    UfCrmTask: '',
    tagsCSV: '',
    // BE-3.1: project приходит облегчённым {entityTypeId,id,title}; маппим в
    // Bitrix-форму Project, чтобы потребители читали привычные ID/Title.
    project: t.project
      ? ({
          ID: t.project.id,
          EntityTypeID: t.project.entityTypeId,
          Title: { String: t.project.title },
        } as ANY)
      : ({ Title: { String: '' } } as ANY),
    company: t.companyTitle ? ({ Title: t.companyTitle } as ANY) : undefined,
    checklist: [],
    elapsed: [],
    comments: [],
    replicate: 'N',
    controlling: 'N',
    isMeeting: Boolean(t.isMeeting),
    fileStats: { images: 0, videos: 0, documents: 0, others: 0 },
    files: [],
  };

  return { ...base, type: determineTaskType(base, currentUserId) };
}

/** GET /tasks/calendar — облегчённый список задач для вида «Календарь»
 *  (ограничен диапазоном дедлайнов, без total/пагинации и тяжёлого обогащения).
 *  Возвращает Paginated<Task> — drop-in под useTasks/потребителей календаря. */
export async function fetchTasksCalendar(
  params: FetchTasksParams
): Promise<Paginated<Task>> {
  const { data } = await http.get<{ result: TaskCalendarRaw[] }>(
    '/tasks/calendar',
    { params: calendarQueryParams(params) }
  );
  const items = (data?.result ?? []).map((t) =>
    mapCalendarTask(t, params.currentUserId)
  );
  return { items, page: 1, perPage: items.length, total: items.length };
}

function extractBackendTaskId(id: string | number): number | null {
  if (typeof id === 'number' && Number.isFinite(id)) {
    return id;
  }

  if (typeof id !== 'string') {
    return null;
  }

  if (id.startsWith('intermediate-') || id.startsWith('meeting-')) {
    const num = Number(id.replace(/^(intermediate|meeting)-/, ''));
    return Number.isFinite(num) ? num : null;
  }

  const num = Number(id);
  return Number.isFinite(num) ? num : null;
}

/** Первое не-null/undefined значение — чтобы читать поле независимо от регистра
 *  ключа (полный GET /tasks/{id} отдаёт капитализированные Bitrix-поля
 *  ID/Title/Status, а /tasks/{id}/card может отдавать camelCase). */
function pickField(...vals: ANY[]): ANY {
  return vals.find((v) => v !== undefined && v !== null);
}

/** Маппинг сырого объекта задачи (полный GET /tasks/{id} ИЛИ /tasks/{id}/card)
 *  в TaskDetails. Читаем оба регистра ключей, чтобы один маппер покрывал оба
 *  источника. */
function mapRawTaskCore(raw: ANY): { core: TaskDetails; files: ANY[] } {
  const t = raw as ANY;

  const files =
    t.files?.map((f: ANY) => ({
      id: f.id,
      name: f.name,
      size: f.size,
      mimeType: f.mimeType,
      downloadUrl: f.downloadUrl,
      url: f.url,
    })) ?? [];

  const status = pickField(t.Status, t.status);
  const baseDetails: Omit<TaskDetails, 'type'> = {
    id: String(pickField(t.ID, t.id) ?? ''),
    title: getTitleValue(pickField(t.Title, t.title)),
    status: mapStatus(status),
    priority: getNumberValue(pickField(t.Priority, t.priority)),

    hasResult: pickField(t.hasResult, t.HasResult),
    requiresApproval: pickField(t.requiresApproval, t.RequiresApproval),

    fileStats: pickField(t.fileStats, t.FileStats) ?? {
      images: 0,
      videos: 0,
      documents: 0,
      others: 0,
    },

    assigneeId: getNumberValue(pickField(t.ResponsibleID, t.responsibleID)),
    assigneeName: null,

    dueDate: pickDate(pickField(t.Deadline, t.deadline)),
    updatedAt:
      pickDate(pickField(t.ChangedDate, t.changedDate)) ??
      pickDate(pickField(t.ActivityDate, t.activityDate)) ??
      pickDate(pickField(t.CreatedDate, t.createdDate)) ??
      new Date().toISOString(),

    createdAt: pickDate(pickField(t.CreatedDate, t.createdDate)),

    tagsCSV: Array.isArray(t.tags)
      ? t.tags.join(',')
      : (pickField(t.TagsCSV, t.tagsCSV) ?? ''),
    description: getStringValue(pickField(t.Description, t.description)),
    descriptionInBbcode:
      (pickField(t.DescriptionInBbcode, t.descriptionInBbcode) ?? 'N') === 'Y',
    backendStatus: status,
    createdBy: getNumberValue(pickField(t.CreatedBy, t.createdBy)),
    changedDate: pickDate(pickField(t.ChangedDate, t.changedDate)),
    statusChangedDate: pickDate(
      pickField(t.StatusChangedDate, t.statusChangedDate)
    ),

    timeEstimate: pickField(t.TimeEstimate, t.timeEstimate) ?? null,
    activeTimers: Array.isArray(t.activeTimers) ? t.activeTimers : [],
    groupId: getNumberValue(pickField(t.GroupID, t.groupID)),
    stageId: getNumberValue(pickField(t.StageID, t.stageID)),

    accomplices: t.accomplices ?? null,
    auditors: t.auditors ?? null,
    parentId: getNumberValue(pickField(t.ParentID, t.parentID)),
    parentTitle: t.parentTitle ?? null,

    // Особые поля (флажки) — три обычных bool из GET /tasks/{id}.
    isMeeting: !!t.isMeeting,
    specialAttention: !!t.specialAttention,
    techSupport: !!t.techSupport,

    project: t.project,
    dealInfo: t.dealInfo ?? null,
    leadInfo: t.leadInfo ?? null,
    company: t.company,
    UfCrmTask: pickField(t.UfCrmTask, t.ufCrmTask),
    lastMessage: t.lastMessage,
    resume: t.resume,
  };

  const core = {
    ...baseDetails,
    type: determineTaskType(baseDetails, undefined),
    files,
  } as TaskDetails;

  return { core, files };
}

export async function getTask(id: string) {
  const backendTaskId = extractBackendTaskId(id);

  if (!backendTaskId) {
    throw new Error(`getTask called with UI task id: ${id}`);
  }

  const [coreRes, checklistRes, commentsRes, elapsedRes] = await Promise.all([
    http.get(`/tasks/${encodeURIComponent(backendTaskId)}`),
    http.get(`/tasks/${encodeURIComponent(backendTaskId)}/checklist`),
    http.get(`/tasks/${encodeURIComponent(backendTaskId)}/comments`),
    http.get(`/tasks/${encodeURIComponent(backendTaskId)}/elapsed`),
  ]);

  const { core, files } = mapRawTaskCore(coreRes.data);

  return {
    core,
    files,
    checklist: checklistRes.data.result ?? [],
    comments: commentsRes.data.result ?? [],
    elapsed: elapsedRes.data.result ?? [],
  };
}

/** role: "responsible" | "accomplice" | "auditor". */
export type TaskCardTeamMember = Omit<
  DeepRequired<Schema<'tasks_module_internal_models.TaskCardTeamMember'>>,
  'avatar'
> & { avatar: string | null };

export type TaskCardPlanning = DeepRequired<
  Schema<'tasks_module_internal_models.TaskCardPlanning'>
>;

export type TaskCardCounters = DeepRequired<
  Schema<'tasks_module_internal_models.TaskCardTabCounters'>
>;

/** Лёгкая карточка задачи для дефолтного таба «Задача»: ядро + чек-листы +
 *  команда (резолвнута на сервере) + агрегаты планирования + счётчики. Без
 *  comments, полного списка elapsed и (гарантированно) богатых project/deal/lead —
 *  вкладки «Результаты»/«Время»/«Проект» догружаются отдельными эндпоинтами. */
export async function getTaskCard(id: string) {
  const backendTaskId = extractBackendTaskId(id);

  if (!backendTaskId) {
    throw new Error(`getTaskCard called with UI task id: ${id}`);
  }

  const { data } = await http.get(
    `/tasks/${encodeURIComponent(backendTaskId)}/card`
  );
  const t = data as ANY;
  const { core, files } = mapRawTaskCore(t);

  const team: TaskCardTeamMember[] = Array.isArray(t.team)
    ? t.team.map((m: ANY) => ({
        id: Number(pickField(m.id, m.ID) ?? 0),
        name: String(pickField(m.name, m.Name) ?? ''),
        avatar:
          typeof m.avatar === 'string' && m.avatar.trim() ? m.avatar : null,
        role: String(pickField(m.role, m.Role) ?? ''),
      }))
    : [];

  const p = t.planning ?? {};
  const planning: TaskCardPlanning = {
    declaredSeconds: Number(p.declaredSeconds ?? 0),
    spentSeconds: Number(p.spentSeconds ?? 0),
    progressPercent: Number(p.progressPercent ?? 0),
    remainingSeconds: Number(p.remainingSeconds ?? 0),
    overrunSeconds: Number(p.overrunSeconds ?? 0),
  };

  const c = t.tabCounters ?? {};
  const counters: TaskCardCounters = {
    results: Number(c.results ?? 0),
    checklistFiles: Number(c.checklistFiles ?? 0),
    elapsedEntries: Number(c.elapsedEntries ?? 0),
    chatMessages: Number(c.chatMessages ?? 0),
  };

  const checklist = t.checklists ?? t.checklist ?? [];

  return { core, files, checklist, team, planning, counters };
}

export interface TaskProjectResponse {
  /** Обогащённый CRM-родитель задачи. Заполнено ровно одно поле в зависимости от
   *  привязки (смарт-процесс / сделка / лид). Модели те же, что в детальной
   *  задаче — с categorizedFiles, contacts, customFields. */
  project?: ANY;
  dealInfo?: ANY;
  leadInfo?: ANY;
}

/** Данные вкладки «Проект»: обогащённый CRM-родитель (project/deal/lead) с
 *  файлами по бакетам и контактами. Отдельная лёгкая ручка, чтобы не утяжелять
 *  быструю карточку /card — грузится фоном только для этой вкладки. */
export async function fetchTaskProject(
  id: string
): Promise<TaskProjectResponse> {
  const backendTaskId = extractBackendTaskId(id);

  if (!backendTaskId) {
    throw new Error(`fetchTaskProject called with UI task id: ${id}`);
  }

  const { data } = await http.get(
    `/tasks/${encodeURIComponent(backendTaskId)}/project`
  );
  return (data ?? {}) as TaskProjectResponse;
}

// POST /tasks/intermediate и весь трекинг промежуточных дел живут в
// entities/intermediate (отдельная серверная система таймеров).

export async function createTask(
  payload: CreateTaskData & Record<string, ANY>
) {
  const normalized = {
    ...payload,

    AllowTimeTracking:
      payload.AllowTimeTracking ?? payload.ALLOW_TIME_TRACKING ?? 'Y',

    AllowChangeDeadline:
      payload.AllowChangeDeadline ?? payload.ALLOW_CHANGE_DEADLINE ?? 'Y',
  };
  const { data } = await http.post('/tasks/add', normalized);
  return data;
}

export async function updateTask(
  id: string,
  payload: Partial<{
    TITLE?: string;
    STATUS?: number;
    RESPONSIBLE_ID?: number;
    DEADLINE?: string | null;
    START_DATE_PLAN?: string;
    END_DATE_PLAN?: string;
    PARENT_ID?: number | null;
    TIME_ESTIMATE?: number;
    UF_CRM_TASK?: string;
    // Особые поля (флажки) — camelCase, в отличие от остальных ключей.
    isMeeting?: boolean;
    specialAttention?: boolean;
    techSupport?: boolean;
  }>
) {
  const { data } = await http.post(`/tasks/update/${id}`, payload);
  return data;
}

/** Лёгкий вариант задачи для пикеров «Особых полей» (родитель/подзадачи/связи). */
export interface TaskLite {
  id: number;
  title: string;
}

/**
 * Список задач для выпадашек «Особых полей». groupId — кандидаты из той же
 * рабочей группы; parentId — подзадачи конкретной задачи (GET /tasks/list?parentId=).
 */
export async function fetchTaskOptions(params: {
  groupId?: number | null;
  parentId?: number | null;
}): Promise<TaskLite[]> {
  const q: ANY = {};
  if (params.groupId) {
    q.GROUP_ID = params.groupId;
  }
  if (params.parentId != null) {
    q.parentId = params.parentId;
  }
  const { data } = await http.get<BackendListResponse>('/tasks/list', {
    params: q,
  });
  return (data?.result ?? []).map((t) => ({
    id: getNumberValue(t.ID) ?? 0,
    title: getTitleValue(t.Title) || `#${getNumberValue(t.ID) ?? ''}`,
  }));
}

/** Связанные задачи (blockedBy/blocks) — плоский список из GET /tasks/{id}/relations.
 *  direction в спеке описан как string — сужаем до двух реальных значений. */
export type TaskRelation = DeepRequired<
  Schema<'tasks_module_internal_models.TaskRelation'>
>;

export async function fetchTaskRelations(id: string): Promise<TaskRelation[]> {
  const { data } = await http.get(`/tasks/${id}/relations`);
  return Array.isArray(data?.items) ? data.items : [];
}

export async function addTaskRelation(id: string, relatedTaskId: number) {
  const { data } = await http.post(`/tasks/${id}/relations`, { relatedTaskId });
  return data;
}

export async function deleteTaskRelation(
  id: string,
  relatedTaskId: number,
  direction: 'blockedBy' | 'blocks'
) {
  const { data } = await http.delete(
    `/tasks/${id}/relations/${relatedTaskId}`,
    { params: { direction } }
  );
  return data;
}
export function getTaskTypeColorClass(type: TaskType): string {
  switch (type) {
    case 'urgent':
      return 'bg-[#EF4642] text-white';
    case 'important':
      return 'bg-[#E15A11] text-white';
    case 'regular':
      return 'bg-[#E5B702] text-white';
    case 'helping':
    case 'controlling':
    case 'later':
    case 'normal':
    default:
      return 'bg-white text-black';
  }
}
export async function deleteTask(id: string) {
  const { data } = await http.delete(`/tasks/${id}`);
  return data;
}

export async function completeTask(id: string) {
  const { data } = await http.post(`/tasks/${encodeURIComponent(id)}/complete`);

  return data;
}

/** Массовые действия над задачами (POST /tasks/bulk-action). Один эндпоинт на
 *  все действия — конкретное выбирается полем `action`; остальные поля зависят
 *  от него. Ответ всегда одной формы: `succeededIds` + `failed`. Частичный
 *  успех — норма (одна упавшая задача не валит весь запрос), поэтому проверять
 *  надо оба массива, а не только HTTP 200. */
export type BulkActionRequest = { taskIds: number[] } & (
  | { action: 'complete' }
  | { action: 'deadline'; deadline: string }
  | { action: 'control' }
  | { action: 'responsible'; responsibleId: number }
  | { action: 'creator'; createdBy: number }
  | { action: 'auditor'; userIds: number[] }
  | { action: 'accomplice'; userIds: number[] }
  | { action: 'project'; entityTypeId: number; entityId: number }
  | { action: 'delete' }
);

export type BulkActionResponse = DeepRequired<
  Schema<'tasks_module_internal_models.BulkTaskActionResponse'>
>;

export async function bulkAction(
  payload: BulkActionRequest
): Promise<BulkActionResponse> {
  const { data } = await http.post('/tasks/bulk-action', payload);
  return data;
}

export async function fetchUser(id: number) {
  const { data } = await http.get(`/users/${id}`);
  return data;
}

export async function addComment(taskId: string | number, text: string) {
  const { data } = await http.post(`/tasks/${taskId}/comments`, {
    POST_MESSAGE: text,
  });
  return data;
}

export async function toggleChecklistItem(
  taskId: number | string,
  itemId: number,
  isComplete: boolean
) {
  const { data } = await http.post(
    `/tasks/${taskId}/checklist/${itemId}/toggle`,
    { isComplete }
  );
  return data;
}

export async function createChecklist(
  taskId: number | string,
  payload: ChecklistCreatePayload
) {
  const { data } = await http.post(
    `/tasks/${encodeURIComponent(taskId)}/checklists`,
    payload
  );
  return data;
}

export async function deleteChecklist(
  taskId: number | string,
  checklistId: number | string
) {
  const { data } = await http.delete(
    `/tasks/${encodeURIComponent(taskId)}/checklists/${encodeURIComponent(checklistId)}`
  );
  return data;
}

export async function createChecklistItem(
  taskId: number | string,
  payload: ChecklistItemCreatePayload
) {
  const { data } = await http.post(
    `/tasks/${encodeURIComponent(taskId)}/checklist`,
    payload
  );
  return data; // обычно: { result: {...} } или сразу объект — ниже покажу как нормализовать
}

export async function updateChecklistItem(
  taskId: number | string,
  itemId: number,
  payload: ChecklistItemUpdatePayload
) {
  const { data } = await http.put(
    `/tasks/${encodeURIComponent(taskId)}/checklist/${encodeURIComponent(itemId)}`,
    payload
  );
  return data;
}

export async function deleteChecklistItem(
  taskId: number | string,
  itemId: number
) {
  const { data } = await http.delete(
    `/tasks/${encodeURIComponent(taskId)}/checklist/${encodeURIComponent(itemId)}`
  );
  return data;
}

export async function uploadTaskFile(taskId: number | string, file: File) {
  const formData = new FormData();
  formData.append('file', file);

  const { data } = await http.post(`/tasks/${taskId}/files`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return data;
}

export async function approveTask(taskId: string) {
  if (!taskId) {
    throw new Error('approveTask: taskId is required');
  }

  const { data } = await http.post(`/tasks/${taskId}/approve`);

  return data;
}

export async function cloneTask(
  taskId: string,
  payload?: ANY
): Promise<number> {
  const { data } = await http.post(`/tasks/${taskId}/clone`, payload);

  if (!data?.id || typeof data.id !== 'number') {
    throw new Error('cloneTask: не получили id клона');
  }

  return data.id;
}

export type RejectTaskData = {
  message: string;
  files?: {
    content: string;
    name: string;
    targetField: string;
  }[];
};

export const rejectTask = async (
  taskId: string,
  rejectData: RejectTaskData
): Promise<void> => {
  await http.post(`/tasks/${encodeURIComponent(taskId)}/reject`, rejectData);
};

export async function fetchChecklistItemFiles(
  taskId: number | string,
  itemId: number
): Promise<ChecklistItemFile[]> {
  const { data } = await http.get(
    `/tasks/${encodeURIComponent(taskId)}/checklist/${encodeURIComponent(itemId)}/files`
  );

  const list = (data?.result ??
    data?.data?.result ??
    []) as ChecklistItemFile[];
  return Array.isArray(list) ? list : [];
}

/** GET /tasks/{id}/checklist/files — все вложения задачи из всех чеклистов
 *  (общая галерея файлов чеклистов). */
export async function fetchChecklistFiles(
  taskId: number | string
): Promise<ChecklistItemFile[]> {
  const { data } = await http.get(
    `/tasks/${encodeURIComponent(taskId)}/checklist/files`
  );

  const list = (data?.result ??
    data?.data?.result ??
    []) as ChecklistItemFile[];
  return Array.isArray(list) ? list : [];
}

export async function uploadChecklistItemFile(
  taskId: number | string,
  itemId: number,
  payload: ChecklistItemFilePayload
): Promise<ChecklistItemFile[]> {
  const { data } = await http.post(
    `/tasks/${encodeURIComponent(taskId)}/checklist/${encodeURIComponent(itemId)}/files`,
    payload
  );

  const list = (data?.result ?? data?.data?.result ?? null) as
    | ChecklistItemFile[]
    | null;

  return Array.isArray(list) ? list : [];
}

/** DELETE /tasks/{id}/checklist/{itemId}/files/{fileId} — отвязать файл от
 *  пункта чеклиста. */
export async function deleteChecklistItemFile(
  taskId: number | string,
  itemId: number,
  fileId: number
) {
  const { data } = await http.delete(
    `/tasks/${encodeURIComponent(taskId)}/checklist/${encodeURIComponent(itemId)}/files/${encodeURIComponent(fileId)}`
  );
  return data;
}
